import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { ReceivePayment } from '../../../types/bookkeeping.js';

const STATUS_CLASSES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  partial: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
  void: 'bg-slate-100 text-slate-500',
};

export function ReceivePayments() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['bk-receive-payments', activeOrgId],
    queryFn: () =>
      api<{ receive_payments: ReceivePayment[] }>('/bookkeeping/sales/receive-payments?limit=50'),
    enabled: !!activeOrgId,
  });

  const payments = data?.receive_payments ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('sales.receive_payments.title')}</h1>
        <Link
          to="/app/bookkeeping/sales/receive-payments/new"
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('sales.receive_payments.new')}
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('sales.invoices.number')}</th>
                <th className="px-3 py-2">
                  {t('sales.invoices.entry_date', { defaultValue: 'Fecha' })}
                </th>
                <th className="px-3 py-2">{t('sales.invoices.currency')}</th>
                <th className="px-3 py-2 text-right">{t('sales.receive_payments.gross')}</th>
                <th className="px-3 py-2 text-right">
                  {t('sales.receive_payments.withholding_total')}
                </th>
                <th className="px-3 py-2 text-right">{t('sales.receive_payments.net')}</th>
                <th className="px-3 py-2">{t('sales.invoices.status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-slate-400">
                    {t('sales.receive_payments.empty', {
                      defaultValue: 'No hay pagos recibidos aún.',
                    })}
                  </td>
                </tr>
              )}
              {payments.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link
                      to={`/app/bookkeeping/sales/receive-payments/${p.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {p.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{p.payment_date}</td>
                  <td className="px-3 py-2">{p.currency}</td>
                  <td className="px-3 py-2 text-right font-mono">{p.gross_amount}</td>
                  <td className="px-3 py-2 text-right font-mono">{p.withholding_total}</td>
                  <td className="px-3 py-2 text-right font-mono">{p.net_amount}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-sm px-1.5 py-0.5 text-xs ${STATUS_CLASSES[p.status] ?? ''}`}
                    >
                      {t(`sales.invoices.statuses.${p.status}`, { defaultValue: p.status })}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/app/bookkeeping/sales/receive-payments/${p.id}`}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {t('journalEntries.viewEdit')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
